import React, { useEffect, useState } from "react";
import { css } from "@emotion/css";
import type { UiInteractionSpec } from "../../types/state";
import { MathMarkdown } from "../../components/MathMarkdown";
import { PendingIndicator } from "../../components/PendingIndicator";

/**
 * Shared post-answer feedback beat — the single_select model, lifted so
 * EVERY interaction kind behaves the same way.
 *
 * When the learner submits an answer, the backend generates the next
 * interaction whose leading stream field (``preamble`` for single_select
 * / free_text / diagram_puzzle, ``explanation`` for code /
 * sequence_gap_select / ordering) IS the coach's feedback on the answer
 * they just gave — bundled with the next question + observation drafts in
 * one generation.  This gate surfaces that feedback as a streamed bubble
 * with a Continue button, then reveals the interaction's own content.
 *
 * It only engages when there's a prior answered interaction (i.e. the
 * leading field is feedback, not first-turn framing).  On the opening
 * turn the gate is transparent and the renderer shows its own framing.
 */

const feedbackStyle = css`
  font-size: 0.95em;
  color: var(--color-text);
  line-height: 1.6;
  white-space: pre-wrap;
  background: var(--color-bg-subtle);
  border-radius: 12px;
  padding: 16px 18px;
  border-left: 3px solid var(--color-primary);

  p { margin: 0.4em 0; }
  p:first-of-type { margin-top: 0; }
  p:last-of-type { margin-bottom: 0; }
  strong { font-weight: 600; }
  code {
    background: var(--color-bg-raised);
    padding: 1px 5px;
    border-radius: 4px;
    font-size: 0.92em;
  }
  hr { border: none; border-top: 1px solid var(--color-border-subtle); margin: 12px 0; }
`;

const continueBtnStyle = css`
  width: 100%;
  padding: 12px;
  border: none;
  border-radius: 10px;
  background: var(--color-primary);
  color: var(--color-text-inverse);
  font-size: 1em;
  font-weight: 600;
  cursor: pointer;
  margin-top: 12px;
  transition: background 0.15s ease;

  &:hover { background: var(--color-primary-hover); }
`;

const wrapperStyle = css`
  display: flex;
  flex-direction: column;
  padding: 16px 0;
`;

interface FeedbackGateProps {
  interaction: UiInteractionSpec;
  /** True when there's at least one prior answered interaction — i.e.
   *  the leading field carries feedback rather than first-turn framing. */
  hasPriorInteraction: boolean;
  /** True while the next interaction (feedback + question) is streaming. */
  streaming: boolean;
  children: React.ReactNode;
}

export const FeedbackGate: React.FC<FeedbackGateProps> = ({
  interaction,
  hasPriorInteraction,
  streaming,
  children,
}) => {
  // The leading stream field is named ``preamble`` on some kinds and
  // ``explanation`` on others; either way it's where the coach's
  // reaction to the previous answer lands.
  const feedback =
    (interaction as { preamble?: string }).preamble ??
    (interaction as { explanation?: string }).explanation;
  const pending = Boolean(interaction.submission?.pending);
  const interactionId = interaction.interactionId;

  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    setDismissed(false);
  }, [interactionId]);

  const showFeedback = hasPriorInteraction && !pending && !dismissed;
  if (!showFeedback) {
    return <>{children}</>;
  }

  return (
    <div className={wrapperStyle} data-testid="interaction-feedback-phase">
      {feedback ? (
        <div className={feedbackStyle} data-testid="interaction-feedback">
          <MathMarkdown>{feedback}</MathMarkdown>
        </div>
      ) : (
        <PendingIndicator
          variant="block"
          label="Reviewing your answer…"
          data-testid="interaction-feedback-loading"
        />
      )}
      {!streaming && (
        <button
          className={continueBtnStyle}
          onClick={() => setDismissed(true)}
          data-testid="interaction-feedback-continue"
        >
          Continue
        </button>
      )}
    </div>
  );
};
